import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {UserService} from '../../Services/user.service';
import {ToolsBoxService} from '../../Services/tools.service';
import {User} from '../../Models/user.model';
import {BillingHolder} from "../../Models/Holders/billingholder.model";
import { NgClass, DatePipe } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ProgressBarComponent } from '../../ProgressBar/progressbar.component';
import { FirstNamePipe } from '../../Pipes/firstname.pipe';

type BillingStat = { month: string; totalBillable: number; totalOffDuty: number; billed: number, displayInDayFormat: false };

@Component({
    selector: 'tscheck',
    styleUrls: ['tscheck.component.css'],
    templateUrl: 'tscheck.component.html',
    imports: [NgClass, ReactiveFormsModule, FormsModule, ProgressBarComponent, DatePipe, FirstNamePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TSCheckComponent implements OnInit {

  constructor(private userService: UserService, private toolsBoxService: ToolsBoxService, private router: Router, private cdr: ChangeDetectorRef) {
  }
  @Input() selectedUser: User = null;

  users: User[];
  displayedLogin: string;
  selectedYear: number = new Date().getFullYear();
  // Default to previous month (current month rarely has validated timesheets yet)
  selectedMonth: number = new Date().getMonth() || 12;
  displayCurrentYear = true;
  billingStats: BillingStat[];
  isAdmin = false;
  isDefaultSelectedTab = 'selectedUserTab';
  takenCount: number;
  offCount: number;

  userBillingMap = new Map<number, BillingStat[]>();

  readonly monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  ngOnInit() {
    this.toolsBoxService.changeHeaderName("Timesheet Check");
    this.toolsBoxService.tellChildContainsTabs(false);
    this.isAdmin = this.toolsBoxService.getStoredUserProfile() === 'ADMIN';
    this.displayedLogin = this.toolsBoxService.getStoredUserLogin();
    if (this.isAdmin) {
      this.setSelectedTab("allUsersTab");
      this.loadBillingForYear()
    } else {
      this.setSelectedTab('selectedUserTab');
      this.getBilledHours();
    }
  }

  setSelectedTab(tab: string) {
    this.isDefaultSelectedTab = tab;
  }

  switchSelectedYear = (isCurrent: boolean) => {
    if (this.displayCurrentYear === isCurrent) return;
    this.displayCurrentYear = isCurrent;
    this.takenCount = this.offCount = 0;
    this.userBillingMap.clear();
    if (this.isAdmin) {
      this.loadBillingForYear()
    } else {
      this.getBilledHours();
    }
  }

  onMonthChange() {
    this.cdr.markForCheck();
  }

  //initialization of the first tab when NON ADMIN
  getBilledHours = () => {
    const selectedYear = this.displayCurrentYear ? this.selectedYear : this.selectedYear-1;
    let dataForSelectedUser = this.userBillingMap.get(this.selectedUser?.id);
    // In case we have data for this user in the map, there is no need to call the API
    if (!!dataForSelectedUser) {
      this.billingStats = dataForSelectedUser;
      this.cdr.markForCheck();
    } else {

      this.toolsBoxService.tellActionIsInProgress(true);
      this.userService.getUserBilledHours(this.selectedUser, selectedYear).subscribe({
        next: (billingHolder: BillingHolder) => {
          this.toolsBoxService.tellActionIsInProgress(false);
          this.billingStats = billingHolder.billingStatsList as unknown as BillingStat[];
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.toolsBoxService.tellActionIsInProgress(false);
          this.router.navigate(["Error", {errMessage: err.error}]);
        }
      });
    }
  }

  //initialization of both tabs when ADMIN
  loadBillingForYear() {
    const year = this.displayCurrentYear ? this.selectedYear : this.selectedYear - 1;
    this.userService.getBilledHoursForYear(year).subscribe({
      next: (elements: BillingHolder[]) => {
        this.users = elements.map(element => element.user);
        elements.map(element => {
          this.userBillingMap.set(element.user.id, element.billingStatsList as unknown as BillingStat[]);
        })
        // getBilledHoursForYear recreate the list of Users, we have to map selectedUser with a reference to this new list
        if (this.selectedUser) {
          this.selectedUser = this.users.find(u => u.id === this.selectedUser.id) ?? null;
        } else {
          this.selectedUser = this.users.find(u => u.userLogin === this.displayedLogin);
        }
        this.billingStats = this.selectedUser
          ? elements.find(element => element.user.id === this.selectedUser.id)?.billingStatsList
          : null;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.router.navigate(["Error", {errMessage: err.error}]);
      }
    });
  }

  getUserMonthStat(user: User): BillingStat | null {
    const list = this.userBillingMap.get(user.id);
    if (!list) return null;
    const year = this.displayCurrentYear ? this.selectedYear : this.selectedYear - 1;
    const prefix = `${year}-${String(this.selectedMonth).padStart(2, '0')}`;
    return list.find(s => s.month.startsWith(prefix)) ?? null;
  }

  get groupedUsers(): Array<
    { type: 'client'; clientName: string; missions: { missionLabel: string; users: User[] }[] } |
    { type: 'mission'; missionLabel: string; users: User[] }
  > {
    if (!this.users) return [];

    const clientMap = new Map<string, Map<string, User[]>>();
    const noClientMap = new Map<string, User[]>();

    for (const user of this.users) {
      const missionLabel = user.mission?.label || 'Bench';
      const clientName = user.mission?.client;
      if (clientName) {
        if (!clientMap.has(clientName)) clientMap.set(clientName, new Map());
        const missionMap = clientMap.get(clientName);
        if (!missionMap.has(missionLabel)) missionMap.set(missionLabel, []);
        missionMap.get(missionLabel).push(user);
      } else {
        if (!noClientMap.has(missionLabel)) noClientMap.set(missionLabel, []);
        noClientMap.get(missionLabel).push(user);
      }
    }

    type GroupedEntry =
      { type: 'client'; clientName: string; missions: { missionLabel: string; users: User[] }[] } |
      { type: 'mission'; missionLabel: string; users: User[] };
    const result: GroupedEntry[] = [];

    Array.from(clientMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([clientName, missionMap]) => {
        const missions = Array.from(missionMap.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([missionLabel, users]) => ({ missionLabel, users }));
        result.push({ type: 'client', clientName, missions });
      });

    Array.from(noClientMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([missionLabel, users]) => {
        result.push({ type: 'mission', missionLabel, users });
      });

    return result;
  }

  getProgressbarLabel = (paramOne: number, paramTwo: number, displayInDayFormat: boolean) => {
    let inDays: string = (paramOne/8) + " days / " +(paramTwo/8) + " days";
    let inHours: string = paramOne + " hours / " + paramTwo + " hours";
    if (displayInDayFormat) {
      return inDays + " - " + inHours;
    } else {
      return inHours;
    }
  }

  calculateProgress(paramOne: number, paramTwo: number): number {
    if (!paramTwo) return 0;
    const result = (paramOne / paramTwo) * 100;
    return result > 100 ? 100 : result;
  }

  downloadTimesheet(month: string) {
      const selectedYear = this.displayCurrentYear ? this.selectedYear : this.selectedYear-1;
      this.userService.getTimesheetExport(selectedYear, month).subscribe();
  }
}
